import axios, { AxiosInstance, AxiosResponse } from "axios";
import * as Sentry from "@sentry/react";
import { resolveTenant } from "../config/tenant";
import type {
  AdminBookingsQueueParams,
  AdminBookingsQueueResult,
  AdminAssignBedSpaceRequest,
  AuthResponse,
  BookingStats,
  AssignableStudent,
  ForgotPasswordResponse,
  LoginRequest,
  RegisterRequest,
  ApiResponse,
  BulkImportResult,
  Booking,
  BedSpace,
  ConfirmBookingPaymentRequest,
  ConfirmBookingPaymentBatchRequest,
  VerifyBookingPaymentRequest,
  Hostel,
  HostelRoom,
  AvailableHostelRoom,
  UpdateHostelBallotingRequest,
  UpdateHostelDetailsRequest,
  UpdateHostelRoomConfigRequest,
  BulkUpdateHostelRoomBookingRequest,
  BulkUpdateHostelRoomGenderRequest,
  HostelGender,
  BulkUpdateHostelRoomPricesRequest,
  CreateBallotEntryRequest,
  CreateMyBallotEntryRequest,
  BallotEntry,
  BallotHostelStatus,
  BallotDrawDetail,
  BallotDrawReplayResult,
  ConfirmBallotResultsRequest,
  ConfirmBallotResultsResult,
  CreateSupportTicketRequest,
  ChangeStudentEmailResponse,
  FlutterwaveCheckoutPayload,
  RunBallotDrawRequest,
  RunBallotDrawResult,
  ReserveMyBedSpaceRequest,
  ReserveMyBedSpaceBatchRequest,
  BookingPaymentGroupResult,
  BlacklistedStudent,
  CreateBlacklistPayload,
  BulkBlacklistPayload,
  BulkBlacklistResult,
  BlacklistQueryParams,
  BlacklistStats,
  InviteStaffRequest,
  InviteStaffResponse,
  UpdateBedSpaceAdminReservationRequest,
  StudentSelfProfile,
  StaffHostelAssignment,
  StaffUserWithAssignments,
  SupportTicket,
  SupportTicketAdminListResult,
  SupportTicketListResult,
  NotificationListResult,
  UpdateStudentSelfProfileRequest,
  UpdateSupportTicketRequest,
  VerifyFlutterwavePaymentRequest,
  PaystackCheckoutPayload,
  VerifyPaystackPaymentRequest,
  PublicSettings,
  UpdateSettingsRequest,
  HostelImage,
} from "../types";

class ApiService {
  private api: AxiosInstance;

  constructor() {
    const baseURL =
      import.meta.env.VITE_API_URL || "http://localhost:3000/api/v1";

    // Log the API URL in development for debugging
    if (import.meta.env.DEV) {
      console.log("🔗 API Base URL:", baseURL);
    }

    this.api = axios.create({
      baseURL,
      timeout: 10000, // Default timeout for regular operations
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("access_token");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    // Response interceptor to handle errors
    this.api.interceptors.response.use(
      (response: AxiosResponse<ApiResponse<any>>) => response,
      (error) => {
        const requestUrl = String(error?.config?.url || "");
        // These endpoints don't require (or don't reflect) the caller's own
        // session: a 401 from them means "this token/credential was bad",
        // not "your login session died" — e.g. /auth/verify-email is public
        // and 401s whenever an email link's token is stale/already used,
        // which has nothing to do with whether the clicker happens to be
        // logged in elsewhere. Treating that as a session-kill wipes a
        // perfectly valid session out from under the user.
        const isAuthEndpoint =
          requestUrl.includes("/auth/login") ||
          requestUrl.includes("/auth/register") ||
          requestUrl.includes("/auth/forgot-password") ||
          requestUrl.includes("/auth/reset-password") ||
          requestUrl.includes("/auth/refresh") ||
          requestUrl.includes("/auth/verify-email");

        // Report unexpected failures to Sentry: no response at all (network
        // failure, CORS block, timeout) or a server-side 5xx. This mirrors
        // the backend's own threshold (global-exception.filter.ts) so
        // expected 4xx errors (bad input, wrong password, etc.) - which the
        // caller already surfaces in the UI - don't create noise.
        const status = error?.response?.status;
        if (!status || status >= 500) {
          Sentry.withScope((scope) => {
            scope.setTag("api.method", String(error?.config?.method || "unknown").toUpperCase());
            scope.setTag("api.url", requestUrl);
            if (status) scope.setTag("api.status", String(status));
            scope.setContext("api_error", {
              url: requestUrl,
              method: error?.config?.method,
              status: status ?? null,
              code: error?.code,
            });
            Sentry.captureException(error);
          });
        }

        if (error.response?.status === 401 && !isAuthEndpoint) {
          const token = localStorage.getItem("access_token");
          const savedUser = localStorage.getItem("user");
          const hadSession = Boolean(token || savedUser);

          // localStorage is shared across tabs. A 401 for a request sent with
          // an older token (a stale tab, or one in flight across a fresh
          // login) must not wipe the newer session that replaced it.
          const sentAuth = String(error.config?.headers?.Authorization || "");
          const sentToken = sentAuth.startsWith("Bearer ") ? sentAuth.slice(7) : null;
          if (token && sentToken !== token) {
            return Promise.reject(error);
          }

          if (hadSession) {
            localStorage.removeItem("access_token");
            localStorage.removeItem("user");
          }

          const path = window.location.pathname;
          const protectedAdminPathPrefixes = [
            "/dashboard",
            "/students",
            "/student-import",
            "/facilities",
            "/payments",
            "/bank-statements",
            "/settings",
            "/admin/hostels",
            "/admin/booking-reviews",
            "/admin/allocations",
            "/support-tickets",
            "/admin/staff-access",
          ];
          const isAdminPath = protectedAdminPathPrefixes.some((prefix) =>
            path.startsWith(prefix),
          );
          const isStudentProtectedPath =
            path.startsWith("/student/") || path === "/support";

          // Do not force guest users away from public pages (landing/booking/auth pages).
          if (!hadSession || (!isAdminPath && !isStudentProtectedPath)) {
            return Promise.reject(error);
          }

          window.location.href = isAdminPath ? "/admin/login" : "/login";
        }
        return Promise.reject(error);
      },
    );
  }

  // Auth endpoints
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await this.api.post<ApiResponse<AuthResponse>>(
      "/auth/login",
      credentials,
    );
    return response.data.data;
  }

  async register(userData: RegisterRequest): Promise<AuthResponse> {
    const response = await this.api.post<ApiResponse<AuthResponse>>(
      "/auth/register",
      userData,
    );
    return response.data.data;
  }

  async resendVerificationEmail(): Promise<{ message: string }> {
    const response = await this.api.post<ApiResponse<{ message: string }>>(
      "/auth/resend-verification",
    );
    return response.data.data;
  }

  async verifyEmail(
    token: string,
  ): Promise<{ message: string; user: AuthResponse["user"] }> {
    const response = await this.api.post<
      ApiResponse<{ message: string; user: AuthResponse["user"] }>
    >("/auth/verify-email", { token });
    return response.data.data;
  }

  async forgotPassword(email: string): Promise<ForgotPasswordResponse> {
    const response = await this.api.post<ApiResponse<ForgotPasswordResponse>>(
      "/auth/forgot-password",
      { email },
    );
    return response.data.data;
  }

  async requestMyPasswordReset(): Promise<ForgotPasswordResponse> {
    const profile = await this.getProfile();
    const email = profile?.email ? String(profile.email).trim() : "";
    if (!email) {
      throw new Error("Authenticated user email is not available");
    }
    return this.forgotPassword(email);
  }

  async resetPassword(
    token: string,
    password: string,
    studentId?: string,
  ): Promise<{ message: string }> {
    const response = await this.api.post<ApiResponse<{ message: string }>>(
      "/auth/reset-password",
      studentId ? { token, password, studentId } : { token, password },
    );
    return response.data.data;
  }

  async getProfile() {
    const response = await this.api.get<ApiResponse<any>>("/auth/profile");
    return response.data.data;
  }

  // Generic CRUD methods
  async get<T>(endpoint: string, params?: any): Promise<T> {
    const response = await this.api.get<ApiResponse<T>>(endpoint, { params });
    return response.data.data;
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    const response = await this.api.post<ApiResponse<T>>(endpoint, data);
    return response.data.data;
  }

  async patch<T>(endpoint: string, data?: any): Promise<T> {
    const response = await this.api.patch<ApiResponse<T>>(endpoint, data);
    return response.data.data;
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    const response = await this.api.put<ApiResponse<T>>(endpoint, data);
    return response.data.data;
  }

  async delete<T>(endpoint: string): Promise<T> {
    const response = await this.api.delete<ApiResponse<T>>(endpoint);
    return response.data.data;
  }

  async upload<T>(endpoint: string, formData: FormData): Promise<T> {
    const response = await this.api.post<ApiResponse<T>>(endpoint, formData);
    return response.data.data;
  }

  // Students endpoints
  async getStudents(params?: any) {
    return this.get("/students", params);
  }

  async getStudent(id: string) {
    return this.get(`/students/${id}`);
  }

  async createStudent(data: any) {
    return this.post("/students", data);
  }

  async updateStudent(id: string, data: any) {
    return this.patch(`/students/${id}`, data);
  }

  async changeStudentEmail(id: string, email: string) {
    return this.patch<ChangeStudentEmailResponse>(
      `/students/${encodeURIComponent(id)}/email`,
      { email },
    );
  }

  async deleteStudent(id: string) {
    return this.delete(`/students/${id}`);
  }

  async getStudentStats(hostelId?: string) {
    return this.get("/students/stats", hostelId ? { hostelId } : undefined);
  }

  async exportStudents(format: "csv" | "excel", search?: string) {
    const response = await this.api.get("/students/export", {
      params: { format, search: search || undefined },
      responseType: "blob",
    });

    const blob = new Blob([response.data], {
      type: String(response.headers["content-type"] || "application/octet-stream"),
    });

    const contentDisposition = String(
      response.headers["content-disposition"] || "",
    );
    const fileNameMatch =
      contentDisposition.match(/filename\*=UTF-8''([^;]+)/i) ||
      contentDisposition.match(/filename="?([^"]+)"?/i);
    const fallback = `students-export.${format === "excel" ? "xlsx" : "csv"}`;
    const fileName = decodeURIComponent(fileNameMatch?.[1] || fallback);

    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.setAttribute("download", fileName);
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  }

  async createStudentsBulk(data: any[]): Promise<BulkImportResult> {
    // Use extended timeout for bulk operations
    const originalTimeout = this.api.defaults.timeout;
    this.api.defaults.timeout = 60000; // 60 seconds for bulk operations

    try {
      const result = await this.post<BulkImportResult>("/students/bulk", {
        students: data,
      });
      return result;
    } finally {
      // Restore original timeout
      this.api.defaults.timeout = originalTimeout;
    }
  }

  async refundStudent(studentId: string, amount: number) {
    return this.post(`/students/${studentId}/refund`, { amount });
  }

  // Blacklist endpoints
  async getBlacklist(params?: BlacklistQueryParams) {
    return this.get<{ items: BlacklistedStudent[]; total: number; page: number; pages: number }>(
      "/blacklist",
      params,
    );
  }

  async getBlacklistStats() {
    return this.get<BlacklistStats>("/blacklist/stats");
  }

  async addBlacklistStudent(data: CreateBlacklistPayload) {
    return this.post<BlacklistedStudent>("/blacklist", data);
  }

  async bulkAddBlacklistStudents(data: BulkBlacklistPayload) {
    return this.post<BulkBlacklistResult>("/blacklist/bulk", data);
  }

  async blacklistExistingStudent(studentId: string, reason?: string) {
    return this.post<BlacklistedStudent>(
      `/blacklist/from-student/${encodeURIComponent(studentId)}`,
      { reason },
    );
  }

  async getBlacklistEntry(id: string) {
    return this.get<BlacklistedStudent>(`/blacklist/${id}`);
  }

  async updateBlacklistStudent(
    id: string,
    data: Partial<CreateBlacklistPayload> & { isActive?: boolean },
  ) {
    return this.patch<BlacklistedStudent>(`/blacklist/${id}`, data);
  }

  async removeBlacklistStudent(id: string) {
    return this.delete<{ success: boolean; message: string }>(`/blacklist/${id}`);
  }

  // Payments endpoints
  async getPayments(params?: any) {
    return this.get("/payments", params);
  }

  async getPayment(id: string) {
    return this.get(`/payments/${id}`);
  }

  async createPayment(data: any) {
    return this.post("/payments", data);
  }

  async updatePayment(id: string, data: any) {
    return this.patch(`/payments/${id}`, data);
  }

  async deletePayment(id: string) {
    return this.delete(`/payments/${id}`);
  }

  async getPaymentStats(hostelId?: string) {
    return this.get("/payments/statistics", hostelId ? { hostelId } : undefined);
  }

  // Bank Statements endpoints
  async getBankStatements(params?: any) {
    return this.get("/bank-statements", params);
  }

  async getBankStatement(id: string) {
    return this.get(`/bank-statements/${id}`);
  }

  async uploadBankStatement(file: File, bankType?: "JAIZ" | "GENERIC") {
    const formData = new FormData();
    formData.append("file", file);
    if (bankType) {
      formData.append("bankType", bankType);
    }
    return this.upload("/bank-statements/upload", formData);
  }

  async deleteBankStatement(id: string) {
    return this.delete(`/bank-statements/${id}`);
  }

  async reprocessBankStatement(id: string) {
    return this.patch(`/bank-statements/${id}/reprocess`);
  }

  async getBankStatementStats() {
    return this.get("/bank-statements/statistics");
  }

  async getStudentPayments(params?: any) {
    return this.get("/bank-statements/student-payments", params);
  }

  // Verification endpoints
  async getVerifications(params?: any) {
    return this.get("/verification", params);
  }

  async getVerification(id: string) {
    return this.get(`/verification/${id}`);
  }

  async verifyPayment(data: { paymentId: string; referenceId: string }) {
    return this.post("/verification", data);
  }

  async verifyByReference(referenceId: string) {
    return this.post("/verification/verify-reference", { referenceId });
  }

  async createDirectVerification(
    referenceId: string,
    status: "VERIFIED" | "REJECTED" | "FRAUDULENT",
    reason?: string,
  ) {
    return this.post("/verification/direct-verify", {
      referenceId,
      status,
      reason: reason || `Manually marked as ${status.toLowerCase()}`,
    });
  }

  async updateVerification(id: string, data: any) {
    return this.patch(`/verification/${id}`, data);
  }

  async searchByReference(referenceId: string) {
    return this.post("/verification/search-reference", { referenceId });
  }

  async getVerificationStats(hostelId?: string) {
    return this.get("/verification/statistics", hostelId ? { hostelId } : undefined);
  }

  async getFraudulentPayments() {
    return this.get("/verification/fraudulent");
  }

  async markAsFraud(id: string, reason: string) {
    return this.patch(`/verification/${id}/mark-fraud`, { reason });
  }

  // Room Types endpoints
  async getRoomTypes() {
    return this.get("/room-types");
  }

  async getActiveRoomTypes() {
    return this.get("/room-types/active");
  }

  async getRoomType(id: string) {
    return this.get(`/room-types/${id}`);
  }

  async createRoomType(data: {
    name: string;
    capacity: number;
    price: number;
    description?: string;
  }) {
    return this.post("/room-types", data);
  }

  async updateRoomType(id: string, data: any) {
    return this.patch(`/room-types/${id}`, data);
  }

  async deleteRoomType(id: string) {
    return this.delete(`/room-types/${id}`);
  }

  async getRoomTypeStats() {
    return this.get("/room-types/statistics");
  }

  // Enhanced verification with room selection and student attachment
  async verifyWithRoomType(data: {
    referenceId: string;
    roomTypeId: string;
    notes?: string;
    autoCreateStudent?: boolean;
    studentId?: string;
  }) {
    return this.post("/verification/verify-with-room-type", data);
  }

  // Get room types for verification (from verification endpoint)
  async getVerificationRoomTypes() {
    return this.get("/verification/room-types");
  }

  // Legacy method for backward compatibility
  async verifyPaymentWithRoom(data: {
    referenceId: string;
    roomTypeId: string;
    verificationAmount: number;
    notes?: string;
  }) {
    return this.post("/verification/verify-with-room", data);
  }

  // Facilities endpoints
  async setupHostelFacility(data: {
    name: string;
    description?: string;
    address?: string;
    pricePerSemester: number;
    billingPeriod?: "SEMESTER" | "MONTHLY" | "EXTENSION";
    extensionPeriodMonths?: number;
    gender?: "MALE" | "FEMALE" | "MIXED";
    amenities?: string[];
    blocks: Array<{
      name: string;
      description?: string;
      rooms: Array<{
        roomNumber: string;
        description?: string;
        totalBedSpaces: number;
        bedSpacePrice?: number;
      }>;
    }>;
  }) {
    return this.post("/facilities/hostel/setup", data);
  }

  async getPublicSettings() {
    return this.get<PublicSettings>("/settings/public");
  }

  async updateSettings(data: UpdateSettingsRequest) {
    return this.put<PublicSettings>("/settings", data);
  }

  async getHostels() {
    const { brand } = resolveTenant();
    return this.get<Hostel[]>("/facilities/hostels", { brand });
  }

  // Admin view: includes hostels hidden from the student-facing listing.
  async getAdminHostels() {
    return this.get<Hostel[]>("/facilities/admin/hostels");
  }

  async getHostel(hostelId: string) {
    return this.get<Hostel>(`/facilities/hostel/${hostelId}`);
  }

  async getAdminHostel(hostelId: string) {
    return this.get<Hostel>(`/facilities/admin/hostels/${hostelId}`);
  }

  async getAvailableRooms(hostelId: string) {
    return this.get<AvailableHostelRoom[]>(
      `/facilities/hostels/${hostelId}/available-rooms`,
    );
  }

  async updateHostelBalloting(
    hostelId: string,
    data: UpdateHostelBallotingRequest,
  ) {
    return this.patch<Hostel>(
      `/facilities/hostels/${hostelId}/balloting`,
      data,
    );
  }

  async updateHostelDetails(
    hostelId: string,
    data: UpdateHostelDetailsRequest,
  ) {
    return this.patch<Hostel>(`/facilities/hostels/${hostelId}`, data);
  }

  async uploadHostelImages(hostelId: string, files: File[], altText?: string) {
    const formData = new FormData();
    files.forEach((file) => formData.append("images", file));
    if (altText) {
      formData.append("altText", altText);
    }
    return this.upload<HostelImage[]>(
      `/facilities/hostels/${hostelId}/images`,
      formData,
    );
  }

  async deleteHostelImage(hostelId: string, imageId: string) {
    return this.delete<{ id: string; deleted: boolean }>(
      `/facilities/hostels/${hostelId}/images/${imageId}`,
    );
  }

  async setHostelCoverImage(hostelId: string, imageId: string) {
    return this.patch<HostelImage>(
      `/facilities/hostels/${hostelId}/images/${imageId}/cover`,
    );
  }

  async updateHostelRoomConfig(
    hostelId: string,
    roomId: string,
    data: UpdateHostelRoomConfigRequest,
  ) {
    return this.patch<HostelRoom>(
      `/facilities/hostels/${hostelId}/rooms/${roomId}`,
      data,
    );
  }

  async bulkUpdateHostelRoomPrices(
    hostelId: string,
    data: BulkUpdateHostelRoomPricesRequest,
  ) {
    return this.patch<{
      hostelId: string;
      roomCount: number;
      updatedBedSpaces: number;
      bedSpacePrice: number;
    }>(`/facilities/hostels/${hostelId}/rooms/bulk-bed-price`, data);
  }

  async bulkUpdateHostelRoomBooking(
    hostelId: string,
    data: BulkUpdateHostelRoomBookingRequest,
  ) {
    return this.patch<{
      hostelId: string;
      bookingEnabled: boolean;
      updatedRooms: number;
      reservedOthers: number;
    }>(`/facilities/hostels/${hostelId}/rooms/bulk-booking-status`, data);
  }

  async bulkUpdateHostelRoomGender(
    hostelId: string,
    data: BulkUpdateHostelRoomGenderRequest,
  ) {
    return this.patch<{
      hostelId: string;
      gender: HostelGender;
      updatedRooms: number;
    }>(`/facilities/hostels/${hostelId}/rooms/bulk-gender`, data);
  }

  async updateHostelBedSpaceReservation(
    hostelId: string,
    roomId: string,
    bedSpaceId: string,
    data: UpdateBedSpaceAdminReservationRequest,
  ) {
    return this.patch<BedSpace>(
      `/facilities/hostels/${hostelId}/rooms/${roomId}/bed-spaces/${bedSpaceId}`,
      data,
    );
  }

  async deleteHostel(hostelId: string) {
    return this.delete<{ id: string; name: string; deleted: boolean }>(
      `/facilities/hostels/${hostelId}`,
    );
  }

  async deleteHostelRoom(hostelId: string, roomId: string) {
    return this.delete<{ id: string; roomNumber: string; deleted: boolean }>(
      `/facilities/hostels/${hostelId}/rooms/${roomId}`,
    );
  }

  async deleteHostelBlock(hostelId: string, blockId: string) {
    return this.delete<{ id: string; name: string; deleted: boolean }>(
      `/facilities/hostels/${hostelId}/blocks/${blockId}`,
    );
  }

  async deleteHostelBedSpace(
    hostelId: string,
    roomId: string,
    bedSpaceId: string,
  ) {
    return this.delete<{ id: string; bedNumber: string; deleted: boolean }>(
      `/facilities/hostels/${hostelId}/rooms/${roomId}/bed-spaces/${bedSpaceId}`,
    );
  }

  // Balloting endpoints
  async createBallotEntry(data: CreateBallotEntryRequest) {
    return this.post<BallotEntry>("/balloting/entries", data);
  }

  async createMyBallotEntry(data: CreateMyBallotEntryRequest) {
    return this.post<BallotEntry>("/balloting/me/entries", data);
  }

  async getHostelBallotingStatus(hostelId: string) {
    return this.get<BallotHostelStatus>(
      `/balloting/hostels/${hostelId}/status`,
    );
  }

  async getStudentBallotEntry(hostelId: string, studentId: string) {
    return this.get<BallotEntry | null>(
      `/balloting/hostels/${hostelId}/student/${encodeURIComponent(studentId)}/latest`,
    );
  }

  async getMyStudentBallotEntry(hostelId: string) {
    return this.get<BallotEntry | null>(
      `/balloting/me/hostels/${hostelId}/latest`,
    );
  }

  async getBallotEntries(
    hostelId: string,
    params?: { status?: string; limit?: number },
  ) {
    return this.get<BallotEntry[]>(
      `/balloting/hostels/${hostelId}/entries`,
      params,
    );
  }

  async runBallotDraw(hostelId: string, data?: RunBallotDrawRequest) {
    return this.post<RunBallotDrawResult>(
      `/balloting/hostels/${hostelId}/run-draw`,
      data || {},
    );
  }

  async confirmBallotResults(
    hostelId: string,
    data?: ConfirmBallotResultsRequest,
  ) {
    return this.post<ConfirmBallotResultsResult>(
      `/balloting/hostels/${hostelId}/confirm-results`,
      data || {},
    );
  }

  async getBallotDraw(drawReference: string) {
    return this.get<BallotDrawDetail>(
      `/balloting/draws/${encodeURIComponent(drawReference)}`,
    );
  }

  async replayBallotDraw(
    drawReference: string,
    params?: { revalidateExternalProof?: boolean },
  ) {
    return this.get<BallotDrawReplayResult>(
      `/balloting/draws/${encodeURIComponent(drawReference)}/replay`,
      params,
    );
  }

  // Bookings endpoints
  async reserveMyBedSpace(data: ReserveMyBedSpaceRequest) {
    return this.post<Booking>("/bookings/me/reserve", data);
  }

  async reserveMyBedSpacesBatch(data: ReserveMyBedSpaceBatchRequest) {
    return this.post<BookingPaymentGroupResult>(
      "/bookings/me/reserve-batch",
      data,
    );
  }

  async adminAssignBedSpace(data: AdminAssignBedSpaceRequest) {
    return this.post<Booking>("/bookings/admin/assign", data);
  }

  async adminReassignBedSpace(
    bookingId: string,
    data: { bedSpaceId: string; notes?: string },
  ) {
    return this.post<Booking>(
      `/bookings/admin/${encodeURIComponent(bookingId)}/reassign-bed`,
      data,
    );
  }

  async confirmBookingPayment(data: ConfirmBookingPaymentRequest) {
    return this.post<Booking>("/bookings/confirm-payment", data);
  }

  async confirmBookingPaymentBatch(data: ConfirmBookingPaymentBatchRequest) {
    return this.post<BookingPaymentGroupResult>(
      "/bookings/confirm-payment-batch",
      data,
    );
  }

  async createFlutterwaveCheckout(bookingId: string) {
    return this.post<FlutterwaveCheckoutPayload>(
      `/bookings/${encodeURIComponent(bookingId)}/flutterwave/checkout`,
    );
  }

  async verifyFlutterwavePayment(
    bookingId: string,
    data: VerifyFlutterwavePaymentRequest,
  ) {
    return this.post<Booking>(
      `/bookings/${encodeURIComponent(bookingId)}/flutterwave/verify`,
      data,
    );
  }

  async createPaymentGroupFlutterwaveCheckout(paymentGroupId: string) {
    return this.post<FlutterwaveCheckoutPayload>(
      `/bookings/payment-groups/${encodeURIComponent(paymentGroupId)}/flutterwave/checkout`,
    );
  }

  async verifyPaymentGroupFlutterwavePayment(
    paymentGroupId: string,
    data: VerifyFlutterwavePaymentRequest,
  ) {
    return this.post<BookingPaymentGroupResult>(
      `/bookings/payment-groups/${encodeURIComponent(paymentGroupId)}/flutterwave/verify`,
      data,
    );
  }

  async createPaystackCheckout(bookingId: string) {
    return this.post<PaystackCheckoutPayload>(
      `/bookings/${encodeURIComponent(bookingId)}/paystack/checkout`,
    );
  }

  async verifyPaystackPayment(
    bookingId: string,
    data: VerifyPaystackPaymentRequest,
  ) {
    return this.post<Booking>(
      `/bookings/${encodeURIComponent(bookingId)}/paystack/verify`,
      data,
    );
  }

  async createPaymentGroupPaystackCheckout(paymentGroupId: string) {
    return this.post<PaystackCheckoutPayload>(
      `/bookings/payment-groups/${encodeURIComponent(paymentGroupId)}/paystack/checkout`,
    );
  }

  async verifyPaymentGroupPaystackPayment(
    paymentGroupId: string,
    data: VerifyPaystackPaymentRequest,
  ) {
    return this.post<BookingPaymentGroupResult>(
      `/bookings/payment-groups/${encodeURIComponent(paymentGroupId)}/paystack/verify`,
      data,
    );
  }

  async uploadBookingReceipt(
    bookingId: string,
    file: File,
    notes?: string,
    paymentReference?: string,
    paymentMethod?: "TRANSFER" | "MANUAL",
  ) {
    const formData = new FormData();
    formData.append("receipt", file);
    if (paymentMethod) {
      formData.append("paymentMethod", paymentMethod);
    }
    if (notes) {
      formData.append("notes", notes);
    }
    if (paymentReference) {
      formData.append("paymentReference", paymentReference);
    }
    return this.upload<Booking>(
      `/bookings/${bookingId}/upload-receipt`,
      formData,
    );
  }

  async uploadPaymentGroupReceipt(
    paymentGroupId: string,
    file: File,
    notes?: string,
    paymentReference?: string,
  ) {
    const formData = new FormData();
    formData.append("receipt", file);
    if (notes) {
      formData.append("notes", notes);
    }
    if (paymentReference) {
      formData.append("paymentReference", paymentReference);
    }
    return this.upload<BookingPaymentGroupResult>(
      `/bookings/payment-groups/${encodeURIComponent(paymentGroupId)}/upload-receipt`,
      formData,
    );
  }

  async adminVerifyBooking(
    bookingId: string,
    data: VerifyBookingPaymentRequest,
  ) {
    return this.post<Booking>(`/bookings/admin/${bookingId}/verify`, data);
  }

  async adminVerifyRemainingGroupBookings(
    paymentGroupId: string,
    data: VerifyBookingPaymentRequest,
  ) {
    return this.post<BookingPaymentGroupResult>(
      `/bookings/admin/payment-groups/${encodeURIComponent(paymentGroupId)}/verify-remaining`,
      data,
    );
  }

  async adminReleaseBedAssignment(bookingId: string) {
    return this.post<Booking>(`/bookings/admin/${bookingId}/release-bed`, {});
  }

  async adminTransferBooking(bookingId: string, data: { newStudentId: string; notes?: string }) {
    return this.post<Booking>(`/bookings/admin/${bookingId}/transfer`, data);
  }

  async adminHoldBooking(bookingId: string, data?: { reason?: string }) {
    return this.post<Booking>(`/bookings/admin/${encodeURIComponent(bookingId)}/temporary-hold`, data || {});
  }

  async adminReinstateBooking(bookingId: string, data?: { notes?: string }) {
    return this.post<Booking>(`/bookings/admin/${encodeURIComponent(bookingId)}/reinstate`, data || {});
  }

  async getBooking(bookingId: string) {
    return this.get<Booking>(`/bookings/${bookingId}`);
  }

  // Receipts live in a private bucket, so viewing goes through the backend:
  // it returns a time-limited presigned S3 URL that opens directly in the
  // browser and expires afterwards. When S3 is not configured (local dev),
  // the file is streamed through the authenticated API instead.
  async openBookingReceipt(bookingId: string) {
    const { url } = await this.get<{
      url: string | null;
      expiresInSeconds: number | null;
    }>(`/bookings/${bookingId}/receipt-url`);

    if (url) {
      window.open(url, "_blank", "noopener");
      return;
    }

    const response = await this.api.get(`/bookings/${bookingId}/receipt`, {
      responseType: "blob",
    });

    const blob = new Blob([response.data], {
      type: String(response.headers["content-type"] || "application/octet-stream"),
    });

    const blobUrl = window.URL.createObjectURL(blob);
    window.open(blobUrl, "_blank", "noopener");
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
  }

  async getBookingsByStudentId(studentId: string, limit?: number) {
    return this.get<Booking[]>(
      `/bookings/student/${encodeURIComponent(studentId)}`,
      { limit },
    );
  }

  async getMyBookings(limit?: number) {
    return this.get<Booking[]>("/bookings/user/me", { limit });
  }

  async getAdminBookingsQueue(params?: AdminBookingsQueueParams) {
    return this.get<AdminBookingsQueueResult>("/bookings/admin/queue", params);
  }

  async getBookingStats(hostelId?: string) {
    return this.get<BookingStats>("/bookings/admin/stats", hostelId ? { hostelId } : undefined);
  }

  // Student self profile endpoints
  async getMyStudentProfile() {
    return this.get<StudentSelfProfile>("/students/me/profile");
  }

  async searchAssignableStudents(q: string) {
    return this.get<AssignableStudent[]>("/students/lookup", { q });
  }

  async updateMyStudentProfile(data: UpdateStudentSelfProfileRequest) {
    return this.patch<StudentSelfProfile>("/students/me/profile", data);
  }

  // Staff-hostel scope RBAC endpoints
  async getStaffUsersWithAssignments() {
    return this.get<StaffUserWithAssignments[]>(
      "/users/staff/hostel-assignments",
    );
  }

  async getStaffHostelAssignments(userId: string) {
    return this.get<StaffHostelAssignment>(
      `/users/${encodeURIComponent(userId)}/hostel-assignments`,
    );
  }

  async setStaffHostelAssignments(userId: string, hostelIds: string[]) {
    return this.patch<StaffHostelAssignment>(
      `/users/${encodeURIComponent(userId)}/hostel-assignments`,
      { hostelIds },
    );
  }

  async inviteStaff(data: InviteStaffRequest) {
    return this.post<InviteStaffResponse>("/users/invite-staff", data);
  }

  async deleteUser(userId: string) {
    return this.delete(`/users/${encodeURIComponent(userId)}`);
  }

  // Support endpoints
  async createSupportTicket(
    data: CreateSupportTicketRequest,
    attachments: File[] = [],
  ) {
    const formData = new FormData();
    formData.append("subject", data.subject);
    formData.append("message", data.message);
    if (data.category) formData.append("category", data.category);
    if (data.priority) formData.append("priority", data.priority);
    attachments.forEach((file) => formData.append("attachments", file));
    return this.upload<SupportTicket>("/support/tickets", formData);
  }

  async getSupportAttachmentBlob(attachmentId: string) {
    const response = await this.api.get(
      `/support/attachments/${encodeURIComponent(attachmentId)}`,
      { responseType: "blob" },
    );
    return response.data as Blob;
  }

  async getMySupportTickets(params?: {
    status?: string;
    category?: string;
    priority?: string;
    page?: number;
    limit?: number;
  }) {
    return this.get<SupportTicketListResult>("/support/tickets/me", params);
  }

  async getSupportTickets(params?: {
    status?: string;
    category?: string;
    priority?: string;
    page?: number;
    limit?: number;
  }) {
    return this.get<SupportTicketAdminListResult>("/support/tickets", params);
  }

  async updateSupportTicket(
    ticketId: string,
    data: UpdateSupportTicketRequest,
  ) {
    return this.patch<SupportTicket>(
      `/support/tickets/${encodeURIComponent(ticketId)}`,
      data,
    );
  }

  // Notification endpoints
  async getNotifications(params?: { page?: number; limit?: number }) {
    return this.get<NotificationListResult>("/notifications", params);
  }

  async getUnreadNotificationCount() {
    return this.get<{ unreadCount: number }>("/notifications/unread-count");
  }

  async markNotificationRead(notificationId: string) {
    return this.patch(
      `/notifications/${encodeURIComponent(notificationId)}/read`,
    );
  }

  async markAllNotificationsRead() {
    return this.patch<{ updated: number }>("/notifications/read-all");
  }
}

export const apiService = new ApiService();
export default apiService;
